import jsPDF from 'jspdf';
import LOGO_BASE64 from './logoBase64';

const fmt = (v: number | string): string =>
  Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

interface FotoCarregada { base64: string; w: number; h: number }

function loadImageAsBase64(url: string | null | undefined, maxSize = 240): Promise<FotoCarregada | null> {
  if (!url) return Promise.resolve(null);
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      try {
        const ratio = Math.min(maxSize / img.width, maxSize / img.height, 1);
        const w = Math.max(1, Math.round(img.width * ratio));
        const h = Math.max(1, Math.round(img.height * ratio));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d')!;
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
        resolve({ base64: canvas.toDataURL('image/jpeg', 0.85), w, h });
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

interface BannerCarregado { dataUrl: string; w: number; h: number }

// Banner promocional do rodape (arquivo estatico em /public/banner-orcamento.png).
// Carregado como PNG full-res SEM recompressao — o loadImageAsBase64 acima capa
// em 240px e re-encoda JPEG (ruim pros QR do banner). Falha silenciosa: sem
// banner, o orcamento sai normal.
function carregarBanner(): Promise<BannerCarregado | null> {
  return fetch('/banner-orcamento.png')
    .then((r) => (r.ok ? r.blob() : Promise.reject(new Error('banner nao encontrado'))))
    .then(
      (blob) =>
        new Promise<BannerCarregado | null>((resolve) => {
          const fr = new FileReader();
          fr.onload = () => {
            const dataUrl = fr.result as string;
            const img = new Image();
            img.onload = () => resolve({ dataUrl, w: img.naturalWidth, h: img.naturalHeight });
            img.onerror = () => resolve(null);
            img.src = dataUrl;
          };
          fr.onerror = () => resolve(null);
          fr.readAsDataURL(blob);
        }),
    )
    .catch(() => null);
}

export interface ItemPdf {
  codigo_interno: string | null;
  produto_descricao: string;
  marca_nome?: string | null;
  unidade_sigla?: string | null;
  quantidade: number | string;
  preco_original: number | string;
  preco_unitario: number | string;
  preco_pix?: number | string;
  desconto_percentual?: number | string;
  subtotal: number | string;
  foto_url?: string | null;
}

export interface OrcamentoPdf {
  numero: string;
  created_at: string;
  vendedor_nome?: string | null;
  cliente_nome?: string | null;
  forma_pagamento_nome?: string | null;
  subtotal?: number | string;
  desconto_total_percentual?: number | string;
  desconto_total_valor?: number | string;
  total: number | string;
  observacoes?: string | null;
  itens: ItemPdf[];
}

export default async function gerarPdfOrcamento(orcamento: OrcamentoPdf): Promise<jsPDF> {
  const itens = orcamento.itens || [];

  // Pre-carrega todas as fotos + o banner do rodape em paralelo. Item sem foto fica null.
  const [fotos, banner] = await Promise.all([
    Promise.all(itens.map((it) => loadImageAsBase64(it.foto_url))),
    carregarBanner(),
  ]);

  // ===== Pagina elastica =====
  // O PDF e exportado como JPG e enviado por WhatsApp — imagem comprida nao
  // tem problema, e a quebra de pagina tradicional era confusa pro cliente
  // (rodape isolado, header repetido, etc). Em vez disso, calculamos a altura
  // total que tudo vai ocupar e criamos a pagina sob medida. Curto continua
  // saindo em A4 padrao (297mm); longo cresce o quanto precisar.
  const LARGURA = 210;
  const ALT_MIN = 297;
  const descColW = 59;
  const fotoTamCalc = 9;

  // tempDoc so pra usar splitTextToSize com a mesma fonte do real
  const tempDoc = new jsPDF();
  tempDoc.setFontSize(8);
  const descPorItem = itens.map((it, idx) => {
    const lines = tempDoc.splitTextToSize(it.produto_descricao || '', descColW) as string[];
    const lineH = 4, topPad = 3, bottomPad = 3;
    const descBlockH = lines.length * lineH;
    const fotoH = fotos[idx] ? fotoTamCalc : 0;
    const contentH = Math.max(descBlockH, fotoH, lineH);
    return { lines, rowH: topPad + contentH + bottomPad };
  });
  const altItens = descPorItem.reduce((s, l) => s + l.rowH, 0);
  const altHeader = 51; // logo+info ate antes da tabela
  const altTabelaHeader = 5;
  const altFooterBase = 35; // separador + subtotal + desconto + total
  const altObs = orcamento.observacoes
    ? 14 + ((tempDoc.splitTextToSize(orcamento.observacoes, LARGURA - 28) as string[]).length * 4)
    : 0;
  // Banner full-bleed no rodape: ocupa a largura inteira da pagina; a altura sai
  // proporcional ao PNG. Some no calculo elastico pra pagina crescer e nao cortar.
  const gapBanner = 8; // respiro entre o conteudo e o banner
  const bannerHmm = banner ? LARGURA * (banner.h / banner.w) : 0;
  const altMargemInferior = 10;
  const altRodapePos = banner ? gapBanner + bannerHmm : altMargemInferior;
  const altTotal = altHeader + altTabelaHeader + altItens + altFooterBase + altObs + altRodapePos;
  const ALT_FINAL = Math.max(ALT_MIN, Math.ceil(altTotal));

  const doc = new jsPDF({ unit: 'mm', format: [LARGURA, ALT_FINAL] });
  doc.setLineWidth(0.3);
  const w = doc.internal.pageSize.getWidth();
  let y = 10;

  // === HEADER ===
  const logoW = 24;
  const logoH = logoW * (157 / 183);
  doc.addImage(LOGO_BASE64, 'PNG', 14, y, logoW, logoH);

  const infoX = 38;
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.text('Av Rio da Prata 920, Setor Montes Belos', infoX, y + 8);
  doc.text('64 9 9259-7498', infoX, y + 12);
  doc.text('CNPJ 49.762.154/0001-00', infoX, y + 16);

  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('ORCAMENTO', w - 14, y + 6, { align: 'right' });

  const numLimpo = orcamento.numero.replace('ORC-', '');
  const dataObj = new Date(orcamento.created_at);
  const dataStr = dataObj.toLocaleDateString('pt-BR');
  const horaStr = dataObj.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`N° ${numLimpo}`, w - 14, y + 12, { align: 'right' });
  doc.setFontSize(8);
  doc.text(`${dataStr}`, w - 14, y + 16, { align: 'right' });
  doc.text(`${horaStr}`, w - 14, y + 20, { align: 'right' });

  y += 25;
  doc.setDrawColor(90);
  doc.line(14, y, w - 14, y);
  y += 6;

  // === INFO: Vendedor, Cliente, Pagamento ===
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('Vendedor:', 14, y);
  doc.setFont('helvetica', 'normal');
  doc.text(orcamento.vendedor_nome || '-', 38, y);

  doc.setFont('helvetica', 'bold');
  doc.text('Cliente:', 100, y);
  doc.setFont('helvetica', 'normal');
  doc.text(orcamento.cliente_nome || 'Consumidor', 118, y);

  y += 5;
  doc.setFont('helvetica', 'bold');
  doc.text('Pagamento:', 14, y);
  doc.setFont('helvetica', 'normal');
  doc.text(orcamento.forma_pagamento_nome || '-', 38, y);

  y += 8;

  // Tabela header
  // Gap de 2mm entre toda coluna (Marca antes encostava na Un.). Un. fica ANTES de Marca.
  const cols = [
    { label: '#', x: 14, w: 6, align: 'center' as const },
    { label: 'Foto', x: 20, w: 10, align: 'center' as const },
    { label: 'Codigo', x: 30, w: 10, align: 'left' as const },
    { label: 'Descricao', x: 40, w: 61, align: 'left' as const },
    { label: 'Un.', x: 103, w: 8, align: 'center' as const },
    { label: 'Marca', x: 113, w: 16, align: 'left' as const },
    { label: 'Qtd', x: 131, w: 10, align: 'center' as const },
    { label: 'Unit.', x: 143, w: 15, align: 'right' as const },
    { label: 'C/ Desc.', x: 160, w: 15, align: 'right' as const },
    { label: 'Subtotal', x: 177, w: 19, align: 'right' as const },
  ];

  doc.setFillColor(240, 240, 240);
  doc.rect(14, y - 4, w - 28, 7, 'F');
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  cols.forEach((col) => {
    if (col.align === 'right') {
      doc.text(col.label, col.x + col.w, y, { align: 'right' });
    } else if (col.align === 'center') {
      doc.text(col.label, col.x + col.w / 2, y, { align: 'center' });
    } else {
      doc.text(col.label, col.x, y);
    }
  });

  y += 5;
  doc.setFont('helvetica', 'normal');

  const fotoCol = cols[1]!;
  const descCol = cols[3]!;
  const fotoTam = 9;

  itens.forEach((item, idx) => {
    doc.setFontSize(8);

    const descLines = descPorItem[idx]!.lines;
    const lineH = 4;
    const ascent = 3;
    const topPad = 3;
    const bottomPad = 3;

    const descBlockH = descLines.length * lineH;
    const fotoH = fotos[idx] ? fotoTam : 0;
    const contentH = Math.max(descBlockH, fotoH, lineH);
    const rowH = topPad + contentH + bottomPad;

    if (fotos[idx]) {
      const fotoX = fotoCol.x + (fotoCol.w - fotoTam) / 2;
      const fotoY = y + topPad + (contentH - fotoTam) / 2;
      doc.addImage(fotos[idx]!.base64, 'JPEG', fotoX, fotoY, fotoTam, fotoTam);
    }

    const textBaselineY = y + topPad + (contentH - descBlockH) / 2 + ascent;

    // Unit. = preco CARTAO cheio (preco_original). O orcamento QUOTA o cartao;
    // qualquer desconto sai DESSE valor (piso = pix), nunca em cima do pix.
    const precoMostrado = Number(item.preco_original) > 0 ? Number(item.preco_original) : Number(item.preco_unitario);
    const subMostrado = precoMostrado * Number(item.quantidade);
    // C/ Desc. = preco unitario ja com o desconto aplicado (so exibicao, nao recalcula nada).
    const precoComDesc = Number(item.preco_unitario) > 0 ? Number(item.preco_unitario) : precoMostrado;

    const fields = [
      { text: String(idx + 1), col: cols[0]! },
      { text: item.codigo_interno || '', col: cols[2]! },
      { text: item.unidade_sigla || '-', col: cols[4]! },
      { text: (item.marca_nome || '-').substring(0, 12), col: cols[5]! },
      { text: String(Number(item.quantidade)), col: cols[6]! },
      { text: `R$ ${fmt(precoMostrado)}`, col: cols[7]! },
      { text: `R$ ${fmt(precoComDesc)}`, col: cols[8]! },
      { text: `R$ ${fmt(subMostrado)}`, col: cols[9]! },
    ];

    fields.forEach((r) => {
      if (r.col.align === 'right') {
        doc.text(r.text, r.col.x + r.col.w, textBaselineY, { align: 'right' });
      } else if (r.col.align === 'center') {
        doc.text(r.text, r.col.x + r.col.w / 2, textBaselineY, { align: 'center' });
      } else {
        doc.text(r.text, r.col.x, textBaselineY);
      }
    });

    doc.text(descLines, descCol.x, textBaselineY);

    y += rowH;
    doc.setDrawColor(115);
    doc.line(14, y, w - 14, y);
  });

  y += 3;

  const xLabel = 140;
  const subtotalBruto = itens.reduce(
    (acc, it) => acc + Number(it.preco_original || it.preco_unitario) * Number(it.quantidade),
    0,
  );
  // Bruto = cartao cheio; Desconto = quanto caiu ate o liquido; Liquido = total final.
  // Desconto sempre SOBRE o cartao (% em cima do cheio), nunca sobre o pix.
  const descontoValor = Math.max(0, subtotalBruto - Number(orcamento.total));
  const temDesconto = descontoValor > 0.005;
  const descontoPerc = subtotalBruto > 0 ? (descontoValor / subtotalBruto) * 100 : 0;

  doc.setDrawColor(75);
  doc.line(xLabel - 2, y, w - 14, y);
  y += 6;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text('Bruto:', xLabel, y);
  doc.text(`R$ ${fmt(subtotalBruto)}`, w - 14, y, { align: 'right' });

  // Desconto sempre exibido (mesmo zero), pra deixar claro que o desconto sai do Bruto.
  y += 5;
  if (temDesconto) doc.setTextColor(200, 0, 0);
  doc.text(temDesconto ? `Desconto (${fmt(descontoPerc)}%):` : 'Desconto:', xLabel, y);
  doc.text(`${temDesconto ? '-' : ''}R$ ${fmt(descontoValor)}`, w - 14, y, { align: 'right' });
  doc.setTextColor(0);

  y += 4;
  doc.setDrawColor(75);
  doc.line(xLabel - 2, y, w - 14, y);
  y += 6;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('Liquido:', xLabel, y);
  doc.setTextColor(0, 0, 180);
  doc.text(`R$ ${fmt(orcamento.total)}`, w - 14, y, { align: 'right' });
  doc.setTextColor(0);

  if (orcamento.observacoes) {
    y += 10;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text('Observacoes:', 14, y);
    doc.setFont('helvetica', 'normal');
    y += 4;
    const lines = doc.splitTextToSize(orcamento.observacoes, w - 28);
    doc.text(lines, 14, y);
  }

  // Banner promocional, encostado no rodape (full-bleed, largura da pagina).
  if (banner) {
    const bannerY = ALT_FINAL - bannerHmm;
    doc.addImage(banner.dataUrl, 'PNG', 0, bannerY, LARGURA, bannerHmm);
  }

  return doc;
}
